import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { TabProvider } from './contexts/TabContext';
import { AIProvider } from './contexts/AIContext';
import { useTabValidation } from './hooks/useTabValidation';
import { useUploadWarning } from './hooks/useBeforeUnload';
import { useSSELifecycle } from './stores/fileStatusStore';
import { useUploadProgressStore } from './stores/uploadProgressStore';
import { useAgentTreeStore } from './stores/agentTreeStore';
import type { TreeItem } from './types';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FrameShell } from './components/frame/FrameShell';
import { ComponentType, ReactNode, lazy, Suspense, useEffect } from 'react';

// The React SPA serves at `app.valuepath.io` (prod) / `<id>.stacks.valuepath.io` (stacks).
// The marketing site lives on a separate subdomain and is out-of-scope for this router.
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const InvitePage = lazy(() => import('./pages/InvitePage').then(m => ({ default: m.InvitePage })));
const QuickBooksDisconnected = lazy(() => import('./pages/QuickBooksDisconnected').then(m => ({ default: m.QuickBooksDisconnected })));
// The chunks a signed-in screen is drawn from, named once so a load can ask
// for them early (see `preloadScreen`).
const chunks = {
  frame: () => import('./components/frame/AppFrame'),
  workspace: () => import('./components/Layout/WorkspaceLayout'),
  artifact: () => import('./components/workspace/ArtifactRouteHandler'),
  settings: () => import('./components/settings/SettingsPage'),
  agents: () => import('./pages/AgentPages'),
  agentsIndex: () => import('./components/agents/AgentsIndex'),
  pages: () => import('./pages/PagePages'),
  project: () => import('./pages/ProjectPages'),
  liveModels: () => import('./pages/LiveModelPages'),
  routines: () => import('./pages/RoutinePages'),
};

/**
 * Ask for the frame and the screen at this address while the session is
 * still being restored. Left to `lazy`, each chunk is requested only once the
 * one before it has rendered — sign-in, then the frame, then the screen — a
 * waterfall the person watches. Asked for here, they arrive alongside the
 * session. A path no rule names costs nothing: its chunk loads as before.
 */
function preloadScreen(path: string) {
  if (/^\/(login|invite|integrations)(\/|$)/.test(path)) return;
  const wanted: (keyof typeof chunks)[] = ['frame'];
  if (path === '/' || path === '/routines') wanted.push('project');
  else if (path.startsWith('/agents/')) wanted.push('agents', 'workspace', 'artifact', 'pages');
  else if (path === '/agents') wanted.push('agentsIndex');
  else if (path.startsWith('/routines/')) wanted.push('routines');
  else if (path.startsWith('/pages')) wanted.push('pages');
  else if (path.startsWith('/live-models')) wanted.push('liveModels');
  else if (path.startsWith('/settings')) wanted.push('settings');
  for (const name of wanted) void load(name);
}

type Chunks = typeof chunks;
type Module<K extends keyof Chunks> = Awaited<ReturnType<Chunks[K]>>;
const arrived = new Map<keyof Chunks, unknown>();
function load<K extends keyof Chunks>(name: K): Promise<Module<K>> {
  return chunks[name]().then((m) => {
    arrived.set(name, m);
    return m as Module<K>;
  });
}

/**
 * A screen drawn from one of `chunks`, by its export. Like `lazy`, except that
 * a chunk already here is drawn at once: `lazy` suspends a first render even
 * when the import has settled — it learns so only a tick later — so the frame
 * was drawn with an empty sheet and the screen filled it a moment after. A
 * thenable that answers as it is asked lets `lazy` resolve in that render.
 */
function screen<K extends keyof Chunks, E extends keyof Module<K>>(name: K, exportName: E) {
  type Screen = Extract<Module<K>[E], ComponentType<any>>;
  const pick = (m: Module<K>) => ({ default: m[exportName] as Screen });
  return lazy<Screen>(() => {
    const m = arrived.get(name) as Module<K> | undefined;
    if (m) return { then: (resolve: (v: { default: Screen }) => void) => resolve(pick(m)) } as Promise<{ default: Screen }>;
    return load(name).then(pick);
  });
}
preloadScreen(window.location.pathname);

const WorkspaceLayout = screen('workspace', 'WorkspaceLayout');
const SettingsPage = screen('settings', 'SettingsPage');
const GalleryPage = lazy(() => import('./components/settings/GalleryPage').then(m => ({ default: m.GalleryPage })));
const AgentsIndex = screen('agentsIndex', 'AgentsIndex');
const AppFrame = screen('frame', 'AppFrame');
const AgentDetailPage = screen('agents', 'AgentDetailPage');
const LegacyAgentRedirect = screen('agents', 'LegacyAgentRedirect');
const LegacyHomeRedirect = screen('agents', 'LegacyHomeRedirect');
const PagesIndexPage = screen('pages', 'PagesIndexPage');
const PageScreen = screen('pages', 'PageScreen');
const PageFileRedirect = screen('pages', 'PageFileRedirect');
const RoutinesPage = screen('project', 'RoutinesPage');
const OAuthCallback = lazy(() => import('./components/auth/OAuthCallback').then(m => ({ default: m.OAuthCallback })));
const ArtifactRouteHandler = screen('artifact', 'ArtifactRouteHandler');
const ProjectHomePage = screen('project', 'ProjectHomePage');
const LiveModelsIndexPage = screen('liveModels', 'LiveModelsIndexPage');
const LiveModelDetailPage = screen('liveModels', 'LiveModelDetailPage');
const RoutinePage = screen('routines', 'RoutinePage');
const RunPage = screen('routines', 'RunPage');
const LegacyRoutineRedirect = screen('routines', 'LegacyRoutineRedirect');

/** While a screen is on its way: the empty frame on a signed-in address (the
 *  shape `index.html` painted first), the bare frame colour on a public one. */
function PageLoader() {
  const pub = /^\/(login|invite|integrations\/quickbooks\/disconnected)(\/|$)/.test(window.location.pathname);
  return pub ? <div className="min-h-screen bg-frame" /> : <FrameShell />;
}

// Unauthenticated users get bounced to the marketing subdomain — which lives on a
// different host entirely (e.g. valuepath.io vs app.valuepath.io), so window.location
// is required (React Router's <Navigate> can't cross origins). In local docker dev
// VITE_MARKETING_URL is unset, so we fall through to an in-app /login redirect so the
// dev flow works without a second server running.
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const marketingUrl = import.meta.env.VITE_MARKETING_URL;

  useEffect(() => {
    if (!isLoading && !isAuthenticated && marketingUrl) {
      window.location.replace(marketingUrl);
    }
  }, [isLoading, isAuthenticated, marketingUrl]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    if (!marketingUrl) {
      return <Navigate to="/login" replace />;
    }
    return <PageLoader />;
  }

  return <>{children}</>;
}

function hasSyncFoldersInTree(items: TreeItem[]): boolean {
  for (const item of items) {
    if (item.syncStatus) return true;
    if (item.children && hasSyncFoldersInTree(item.children)) return true;
  }
  return false;
}

/** A synced folder changed: every agent tree on hand is read again. */
const refreshLoadedTrees = () => useAgentTreeStore.getState().refreshLoaded();

function AppLifecycleHooks({ children }: { children: ReactNode }) {
  const { addOptimisticItem } = useProject();
  // The stream stays open for synced folders in any agent tree on hand; an
  // agent nobody has opened shows nothing a sync could change.
  const hasSyncFolders = useAgentTreeStore((s) => Object.values(s.trees).some(hasSyncFoldersInTree));
  useTabValidation();
  useUploadWarning();
  useSSELifecycle(addOptimisticItem, hasSyncFolders, refreshLoadedTrees);

  useEffect(() => {
    useUploadProgressStore.getState().clearOrphanedUploads();
  }, []);

  return <>{children}</>;
}

function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ProjectProvider>
      <TabProvider>
        <AIProvider>
          <AppLifecycleHooks>{children}</AppLifecycleHooks>
        </AIProvider>
      </TabProvider>
    </ProjectProvider>
  );
}

function AuthenticatedRoutes() {
  // A conversation, a file or a deck opened from an agent. Every one is
  // wrapped alike, so moving between them keeps the screen mounted. A file
  // that is a page opens at /pages/:pageId instead.
  const workspace = (type?: 'conversation') => (
    <PageFileRedirect>
      <ArtifactRouteHandler type={type}>
        <WorkspaceLayout />
      </ArtifactRouteHandler>
    </PageFileRedirect>
  );
  return (
    <Routes>
      {/* The frame: sidebar, the main sheet, and the agent drawer. Every
          signed-in screen renders in it. */}
      <Route element={<AppFrame />}>
        <Route path="/" element={<ProjectHomePage />} />

        <Route path="/agents" element={<AgentsIndex />} />
        <Route path="/agents/:agentId" element={<AgentDetailPage />} />
        <Route path="/agents/:agentId/chat/new" element={workspace('conversation')} />
        <Route path="/agents/:agentId/chat/:artifactId" element={workspace('conversation')} />
        <Route path="/agents/:agentId/:artifactType/:artifactId" element={workspace()} />

        <Route path="/routines" element={<RoutinesPage />} />
        <Route path="/routines/:routineId" element={<RoutinePage />} />
        <Route path="/routines/:routineId/runs/:runId" element={<RunPage />} />

        <Route path="/pages" element={<PagesIndexPage />} />
        <Route path="/pages/:pageId" element={<PageScreen />} />
        <Route path="/live-models" element={<LiveModelsIndexPage />} />
        <Route path="/live-models/:projectId/:slug" element={<LiveModelDetailPage />} />
        <Route path="/data" element={<Navigate to="/settings?tab=integrations" replace />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* Older addresses, kept landing. */}
        <Route path="/agent/*" element={<LegacyAgentRedirect />} />
        <Route path="/routine/:routineId" element={<LegacyRoutineRedirect />} />
        <Route path="/routine/:routineId/run/:runId" element={<LegacyRoutineRedirect />} />
        <Route path="/home" element={<LegacyHomeRedirect />} />
        <Route path="/conversation/new" element={<LegacyHomeRedirect />} />
        <Route path="/:artifactType/:artifactId" element={<LegacyHomeRedirect />} />
      </Route>
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/auth/callback/:provider" element={<OAuthCallback />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  // Its own boundary, under the providers: a lazy screen suspending here must
  // not hold the providers back from mounting, or the workspace is asked for
  // only once the frame's chunk has arrived rather than alongside it.
  return (
    <OpenFeatureProvider>
      <AppProviders>
        <Suspense fallback={<FrameShell />}>
          <AuthenticatedRoutes />
        </Suspense>
      </AppProviders>
    </OpenFeatureProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      {/* Baseline <title> for any route that doesn't set its own via the SEO helper.
          Without this, react-helmet-async doesn't always revert after a page like
          /login unmounts, leaving the previous route's title ("Sign in | valuepath")
          stuck in the browser tab even after the user is inside the workspace. */}
      <Helmet defaultTitle="valuepath" />
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public-but-app routes — reachable without login, still part of the SPA. */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/invite" element={<InvitePage />} />
                  {/* Intuit's disconnect URL — public: whoever disconnected in QuickBooks may have no session. */}
                  <Route path="/integrations/quickbooks/disconnected" element={<QuickBooksDisconnected />} />

                  {/* Authenticated app */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <AuthenticatedApp />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
